import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  useFindingFeedback,
  type Finding, type FindingFeedbackBody, type TrustedFactDraft,
} from "@/lib/queries";
import { AuditMarkdown } from "@/components/AuditMarkdown";
import { FindingSourceBadge } from "@/components/FindingSourceBadge";

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-red-500/10 text-red-600 border-red-500/20 dark:text-red-400",
  high:     "bg-orange-500/10 text-orange-600 border-orange-500/20 dark:text-orange-400",
  medium:   "bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400",
  low:      "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400",
  info:     "bg-blue-500/10 text-blue-600 border-blue-500/20 dark:text-blue-400",
};

const STATUS_STYLES: Record<string, string> = {
  open:      "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30",
  confirmed: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30",
  dismissed: "bg-muted text-muted-foreground border-border",
  resolved:  "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
};

const VERDICT_OPTIONS: { value: FindingFeedbackBody["verdict"]; label: string; tone: string; help: string }[] = [
  {
    value: "confirmed", label: "Confirm",
    tone: "border-amber-500/40 hover:bg-amber-500/10 hover:border-amber-500",
    help: "Real signal. I'm acting on it.",
  },
  {
    value: "dismissed", label: "Dismiss",
    tone: "border-emerald-500/40 hover:bg-emerald-500/10 hover:border-emerald-500",
    help: "False positive / explained / benign.",
  },
  {
    value: "resolved", label: "Resolved",
    tone: "border-blue-500/40 hover:bg-blue-500/10 hover:border-blue-500",
    help: "Fixed. Case closed.",
  },
];

function fmt(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export function FindingDetailDrawer({
  finding, open, onOpenChange,
}: {
  finding: Finding | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [verdict, setVerdict] = useState<FindingFeedbackBody["verdict"] | null>(null);
  const [notes, setNotes] = useState("");
  const [trustedActors, setTrustedActors] = useState<Set<string>>(new Set());
  const [trustedHosts, setTrustedHosts] = useState<Set<string>>(new Set());
  const submit = useFindingFeedback();

  if (!finding) return null;

  function reset() {
    setVerdict(null);
    setNotes("");
    setTrustedActors(new Set());
    setTrustedHosts(new Set());
    submit.reset();
  }

  function toggle<T>(set: Set<T>, value: T): Set<T> {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  }

  async function onSubmit() {
    if (!verdict || !finding) return;
    const trust_facts: TrustedFactDraft[] = [];
    if (verdict === "dismissed") {
      for (const a of trustedActors) {
        // Heuristic: looks like an IP → actor_ip; otherwise actor_user.
        const isIp = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(a) || a.includes(":");
        trust_facts.push({
          kind: isIp ? "actor_ip" : "actor_user",
          subject: a,
          reason: notes.trim() || `dismissed via finding #${finding.id}`,
        });
      }
      for (const h of trustedHosts) {
        trust_facts.push({
          kind: "host_pair",
          subject: `${finding.affected_actors[0] ?? "any"}→${h}`,
          reason: notes.trim() || `dismissed via finding #${finding.id}`,
        });
      }
    }
    await submit.mutateAsync({
      id: finding.id,
      body: { verdict, notes: notes.trim(), trust_facts },
    });
    reset();
    onOpenChange(false);
  }

  const refsArr = Object.entries(finding.refs ?? {});

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <SheetContent>
        <SheetHeader>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide",
                SEVERITY_STYLES[finding.severity],
              )}
            >
              {finding.severity}
            </span>
            <span
              className={cn(
                "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                STATUS_STYLES[finding.status],
              )}
            >
              {finding.status}
            </span>
            <FindingSourceBadge source={finding.source} />
            {finding.category && (
              <span className="font-mono text-xs text-muted-foreground">{finding.category}</span>
            )}
          </div>
          <SheetTitle className="mt-2">{finding.title}</SheetTitle>
          <SheetDescription>
            {finding.source === "audit" && finding.audit_id ? (
              <>
                Finding #{finding.ordinal} from{" "}
                <Link to={`/audits?id=${finding.audit_id}`} className="text-primary hover:underline">
                  audit #{finding.audit_id}
                </Link>{" "}
                ({fmt(finding.audit_run_at)})
              </>
            ) : finding.source === "flow_rule" ? (
              <>Auto-promoted from a flow_alert at {fmt(finding.created_at)}</>
            ) : (
              <>Created at {fmt(finding.created_at)}</>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Affected entities */}
          {(finding.affected_hosts.length > 0 || finding.affected_actors.length > 0) && (
            <div className="mb-5 grid grid-cols-[120px,1fr] gap-x-4 gap-y-2 text-sm">
              {finding.affected_hosts.length > 0 && (
                <>
                  <div className="text-muted-foreground">Hosts</div>
                  <div className="flex flex-wrap gap-1.5">
                    {finding.affected_hosts.map((h) => (
                      <span key={h} className="rounded border border-border bg-muted/40 px-2 py-0.5 font-mono text-xs">
                        {h}
                      </span>
                    ))}
                  </div>
                </>
              )}
              {finding.affected_actors.length > 0 && (
                <>
                  <div className="text-muted-foreground">Actors</div>
                  <div className="flex flex-wrap gap-1.5">
                    {finding.affected_actors.map((a) => (
                      <span key={a} className="rounded border border-border bg-muted/40 px-2 py-0.5 font-mono text-xs">
                        {a}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Body */}
          <AuditMarkdown body={finding.body_md} />

          {/* Verdict */}
          {finding.verdict && (
            <div className="mt-6 rounded-md border border-primary/30 bg-primary/5 px-4 py-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">
                Tyrael's recommendation
              </div>
              <div className="mt-1 text-sm">{finding.verdict}</div>
            </div>
          )}

          {/* References */}
          {refsArr.length > 0 && (
            <div className="mt-6">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                References
              </div>
              <pre className="overflow-x-auto rounded-md border border-border bg-muted/40 p-3 text-xs leading-relaxed">
                {JSON.stringify(finding.refs, null, 2)}
              </pre>
            </div>
          )}

          {/* Existing feedback */}
          {finding.user_feedback && (
            <div className="mt-6 rounded-md border border-border bg-muted/30 px-4 py-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Your feedback ({fmt(finding.feedback_at)})
              </div>
              <div className="mt-1 text-sm">{finding.user_feedback}</div>
            </div>
          )}
        </div>

        {/* Feedback form — hidden once a verdict has been recorded */}
        {finding.status === "open" && (
          <div className="border-t border-border bg-muted/20 px-6 py-5">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Your verdict
            </div>

            <div className="grid grid-cols-3 gap-2">
              {VERDICT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setVerdict(opt.value)}
                  title={opt.help}
                  className={cn(
                    "rounded-md border px-3 py-2 text-sm font-medium transition-colors",
                    verdict === opt.value
                      ? "border-primary bg-primary text-primary-foreground"
                      : `border-border bg-background text-foreground ${opt.tone}`,
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="One-sentence note explaining the verdict (e.g. '5.2.230.242 is my home IP — moved to key auth')…"
              rows={3}
              className="mt-3 w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />

            {/* When dismissing, offer to add actors/hosts to trusted_facts so the
                next audit knows not to re-flag them. */}
            {verdict === "dismissed" && (finding.affected_actors.length > 0 || finding.affected_hosts.length > 0) && (
              <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                  Trust these in future audits
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Tyrael will treat these as expected/benign and stop flagging them.
                </div>

                {finding.affected_actors.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {finding.affected_actors.map((a) => {
                      const on = trustedActors.has(a);
                      return (
                        <button
                          key={a}
                          type="button"
                          onClick={() => setTrustedActors((s) => toggle(s, a))}
                          className={cn(
                            "rounded-md border px-2 py-1 font-mono text-xs transition-colors",
                            on
                              ? "border-emerald-500 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                              : "border-border bg-background text-muted-foreground hover:border-emerald-500/40 hover:text-foreground",
                          )}
                        >
                          {on ? "✓ " : "+ "}{a}
                        </button>
                      );
                    })}
                  </div>
                )}

                {finding.affected_hosts.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {finding.affected_hosts.map((h) => {
                      const on = trustedHosts.has(h);
                      return (
                        <button
                          key={h}
                          type="button"
                          onClick={() => setTrustedHosts((s) => toggle(s, h))}
                          className={cn(
                            "rounded-md border px-2 py-1 font-mono text-xs transition-colors",
                            on
                              ? "border-emerald-500 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                              : "border-border bg-background text-muted-foreground hover:border-emerald-500/40 hover:text-foreground",
                          )}
                        >
                          {on ? "✓ " : "+ "}{h}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {submit.isError && (
              <div className="mt-2 text-xs text-red-500">
                Failed to save feedback. Try again.
              </div>
            )}

            <button
              type="button"
              disabled={!verdict || submit.isPending}
              onClick={onSubmit}
              className={cn(
                "mt-3 w-full rounded-md px-4 py-2 text-sm font-medium transition-colors",
                verdict && !submit.isPending
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "cursor-not-allowed bg-muted text-muted-foreground",
              )}
            >
              {submit.isPending ? "Saving…" : verdict ? `Save: ${verdict}` : "Pick a verdict"}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
